import { animate, style, transition, trigger } from "@angular/animations";
import { Component } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs";
import { AbstractBackendTablePage } from "src/app/epicare-public/home/domain/directives/abstract-backend-table-page/abstract-backend-table-page";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { PermissionService } from "src/app/shared/services/permission.service";
import { IBiobank } from "../../domain/interfaces/biobanks.interface";
import { ICancerType } from "../../domain/interfaces/cancer-types.interface";
import { ISampleTemplateInfo } from "../../domain/interfaces/samples.interface";
import { BiobankService } from "../../domain/services/biobank.service";
import { CancerTypeService } from "../../domain/services/cancer-type.service";
import { RnaMutationsService } from "../../domain/services/rna-mutations.service";
import { SamplesService } from "../../domain/services/samples.service";
import { SamplesDataSource } from "./samples-page.datasource";

@Component({
  selector: "app-samples-page",
  templateUrl: "./samples-page.component.html",
  styleUrls: ["./samples-page.component.scss"],
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [style({ opacity: 0 }), animate("600ms ease-in", style({ opacity: 1 }))]),
      transition(":leave", [animate("600ms ease-out", style({ opacity: 0 }))]),
    ]),
  ],
})
export class SamplesPageComponent extends AbstractBackendTablePage<ISampleTemplateInfo> {
  pageSizeOptions = [10, 25, 50, 100];
  sources: IBiobank[] = [];
  cancerTypes: ICancerType[] = [];

  isUploading = false;
  dots = Array(5); // Animation loader
  messages: string[] = [];
  isUploadModalOpen = false;
  selectedSample: ISampleTemplateInfo | null = null;

  constructor(
    private samplesService: SamplesService,
    private biobankService: BiobankService,
    private cancerTypeService: CancerTypeService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    private rnaMutationsService: RnaMutationsService
  ) {
    super();

    this.columns = ["sampleId", "source", "cancerType", "hasRnaSeq", "hasMutation", "metadataCompletion", "uploadDate", "actions"];
  }

  /**
   * Whether the user's role allows modifying a sample's metadata
   */
  get canModifyData(): boolean {
    return this.permissionService.isEnabled(Functionality.MODIFY_DATA);
  }

  /**
   * Whether the user's role allows uploading RNA-seq and mutations for a sample
   */
  get canUploadRnaMutation(): boolean {
    return this.rnaMutationsService.canUpload();
  }

  // -------------- Abstract parent methods ------------------

  /**
   * Initializes the table data source used to fetch samples from the backend.
   */
  public initDataSource(): void {
    this.dataSource = new SamplesDataSource(this.samplesService);
  }

  /**
   * Loads the list of biobanks used as sample sources and the list of cancer types.
   */
  public override initSources(): void {
    this.biobankService
      .getBiobanks()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data: IBiobank[]) => {
          this.sources = data;
        },
        error: () => {
          const message = this.translate.instant("biobank.loadError");
          alert(message);
        },
      });

    this.cancerTypeService
      .getCancerTypes()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data: ICancerType[]) => {
          this.cancerTypes = data;
        },
        error: () => {
          const message = this.translate.instant("cancerType.loadError");
          alert(message);
        },
      });
  }
  // -------------- End of abstract parent methods -----------

  /**
   * Reloads data when a filter changes and resets pagination.
   */
  changeFilter(): void {
    this.currentPage = 1; // Reset
    this.loadData();
  }

  /**
   * Navigates to the page entered by the user.
   * @param event Input event from the page field
   */
  goToPage(event: Event): void {
    const input = event.target as HTMLInputElement;
    const page = Number(input.value);

    if (!page || page < 1) {
      input.value = "1";
      this.changePage(1);
      return;
    }

    if (page > this.nbPages) {
      input.value = this.nbPages.toString();
      this.changePage(this.nbPages);
      return;
    }

    this.changePage(page);
  }

  /**
   * Returns the badge class based on completion value.
   * @param value Metadata completion percentage
   * @return CSS class name for the corresponding completion badge
   */
  getCompletionBadge(value: number): string {
    if (value < 40) return "badge-low";
    if (value < 75) return "badge-medium";
    return "badge-high";
  }

  /**
   * Opens the upload modal for the given sample.
   * @param sample Sample to upload additional data for
   */
  openUploadModal(sample: ISampleTemplateInfo): void {
    this.selectedSample = sample;
    this.isUploadModalOpen = true;
  }

  /**
   * Closes the upload modal and resets its state.
   */
  closeUploadModal(): void {
    this.isUploadModalOpen = false;
    this.selectedSample = null;
    this.messages = [];
  }
}
